import {
  CardCarousel,
  CATEGORY_TABS_CONTAINER_NAME,
  CategoryTabs,
  MOBILE_PRODUCT_CARD_WIDTH,
  PRODUCT_CARD_SIZE_MAP,
  ProductCardSkeleton,
} from '@iheartjane/menu';
import {
  Box,
  Card,
  Flex,
  ReeferThemeProvider,
  SparkleIcon,
} from '@iheartjane/reefer';
import type { MenuComponentConfig } from '@iheartjane/types';
import { MenuTabEnum, SEARCH_POSITION } from '@iheartjane/types';
import type { ReactNode } from 'react';
import { Fragment, useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { getHighlightedSections, getIconColor } from './EditorPreview.util';
import type { SidebarSection } from './EditorSidebar';
import {
  AiButtonSkeleton,
  BUTTON_HEIGHT,
  FilterBarSkeleton,
  FooterSkeleton,
  HeaderSkeleton,
  PreviewSkeleton,
  TEXT_HEIGHT,
} from './sections/EditorPreviewSections';

const DESKTOP_MAX_WIDTH = 1440;
const MOBILE_MAX_WIDTH = 393;
const MOBILE_MAX_HEIGHT = 871;

interface EditorPreviewProps {
  /** Active section of the editor sidebar */
  activeSection: SidebarSection | null;
  isStackedView: boolean;
  isTabletMobileView: boolean;
  sidebarCollapsed: boolean;
  /** View mode of the preview, set by the toggle in the modal header */
  viewMode: 'desktop' | 'mobile';
}

export const EditorPreview = ({
  activeSection,
  isStackedView,
  isTabletMobileView,
  viewMode,
  sidebarCollapsed,
}: EditorPreviewProps) => {
  const { watch } = useFormContext<MenuComponentConfig>();
  const categoryTabsConfig = watch('category_tabs');
  const productCardsConfig = watch('product_cards');
  const searchConfig = watch('search');
  const logoUrl = watch('logo');
  const headerColor = watch('colors.header.main');
  const primaryColor = watch('colors.primary.main');
  const dealsColor = watch('colors.deals.main');

  const isMobile = viewMode === 'mobile';
  const whiteHeader =
    headerColor &&
    (headerColor.toLowerCase() === '#ffffff' ||
      headerColor.toLowerCase() === '#fff');
  const primaryIconColor = getIconColor(primaryColor);
  const headerIconColor = getIconColor(headerColor);
  // Vertical padding accounts for 4px borders for highlighting sections
  // 12 + 4 = 16 on mobile, 20 + 4 = 24 on desktop
  const VERTICAL_PADDING = isMobile || isStackedView ? 12 : 20;
  const HORIZONTAL_PADDING = isMobile || isStackedView ? 16 : 64;

  const highlightedSections = useMemo(
    () => getHighlightedSections(activeSection, searchConfig?.position),
    [activeSection, searchConfig?.position]
  );

  useEffect(() => {
    if (!highlightedSections.length) return;

    const scrollToElement = document.querySelector(
      `#preview-container #${highlightedSections[0]}`
    );
    if (scrollToElement && !isStackedView) {
      scrollToElement.scrollIntoView({
        behavior: 'smooth',
        block: 'nearest',
      });
    }
  }, [highlightedSections, isStackedView]);

  // Sections between the header and filter bar reorder based on settings.
  const sortableSections: Record<string, ReactNode> = {
    category_tabs: (
      <Box
        id="category_tabs"
        data-testid="category_tabs"
        px={HORIZONTAL_PADDING}
        py={VERTICAL_PADDING}
        borderRadius="lg"
        borderWidth="4px"
        border={
          highlightedSections?.includes('category_tabs')
            ? 'purple'
            : 'transparent'
        }
        style={{
          containerName: CATEGORY_TABS_CONTAINER_NAME,
          containerType: 'inline-size',
        }}
      >
        <CategoryTabs
          categories={Object.values(MenuTabEnum)}
          getUrlString={() => ''}
          preview={true}
          primaryColor={primaryColor || ''}
          selectedCategory="featured"
          variant={categoryTabsConfig?.style}
        />
      </Box>
    ),
    full_width_search:
      searchConfig?.position === SEARCH_POSITION.content_header ? (
        <Box
          id="full_width_search"
          data-testid="full_width_search"
          px={HORIZONTAL_PADDING}
          py={VERTICAL_PADDING}
          borderRadius="lg"
          borderWidth="4px"
          border={
            highlightedSections?.includes('full_width_search')
              ? 'purple'
              : 'transparent'
          }
        >
          <Flex
            alignItems="center"
            justifyContent="space-between"
            background="grays-white"
            border="grays-light"
            borderRadius="xl"
            gap={12}
            pl={16}
            pr={8}
            py={8}
            height={isMobile ? BUTTON_HEIGHT : 56}
            width="100%"
          >
            <Flex alignItems="center" gap={12}>
              <PreviewSkeleton height={32} width={32} />
              <PreviewSkeleton height={24} width={100} />
            </Flex>

            {isMobile ? (
              <Flex
                alignItems="center"
                background="primary"
                borderRadius="lg"
                data-testid="ai-button"
                gap={4}
                px={8}
                height={32}
                width={70}
              >
                <SparkleIcon color={primaryIconColor} size="sm" />
                <PreviewSkeleton
                  background="grays-ultralight"
                  height={18}
                  width={34}
                />
              </Flex>
            ) : (
              <AiButtonSkeleton height={40} iconColor={primaryIconColor} />
            )}
          </Flex>
        </Box>
      ) : null,
    carousel: (
      <Box
        id="carousel"
        data-testid="carousel"
        px={HORIZONTAL_PADDING}
        py={VERTICAL_PADDING}
      >
        <PreviewSkeleton height={isMobile ? 250 : 320} width="100%" />

        <Flex justifyContent="center" gap={4} mt={isMobile ? 16 : 24}>
          <PreviewSkeleton background="grays-mid" height={6} width={16} />
          {Array.from({ length: 4 }).map((_, i) => (
            <PreviewSkeleton key={i} height={6} width={6} />
          ))}
        </Flex>
      </Box>
    ),
  };

  const sectionsOrder =
    categoryTabsConfig?.position === 'below_carousel'
      ? ['full_width_search', 'carousel', 'category_tabs']
      : ['category_tabs', 'full_width_search', 'carousel'];

  return (
    <ReeferThemeProvider
      theme={{
        colors: { primary: primaryColor, info: dealsColor },
        borderRadius: {
          sm: '12px',
        },
      }}
      themeId="preview-container"
    >
      <Flex
        id="preview-container"
        justifyContent="center"
        // Account for the sidebar width + gutter
        maxWidth={
          isStackedView
            ? '100%'
            : `calc(100% - ${
                sidebarCollapsed
                  ? '146px'
                  : isTabletMobileView
                    ? '360px'
                    : '400px'
              })`
        }
        width="100%"
        pb={isStackedView ? 56 : 0}
      >
        <Card
          data-testid="preview-content"
          border="grays-light"
          borderWidth="0"
          height="100%"
          width="100%"
          style={{
            '--card-border-radius': 'var(--border-radius-lg)',
            maxWidth: isMobile ? MOBILE_MAX_WIDTH : DESKTOP_MAX_WIDTH,
            maxHeight: isMobile ? MOBILE_MAX_HEIGHT : 'none',
          }}
        >
          <Flex
            background="grays-white"
            flexDirection="column"
            overflow="scroll"
          >
            {/* Header */}
            <Flex
              id="header"
              data-testid="header"
              alignItems="center"
              flexDirection="column"
              gap={24}
              width="100%"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderWidth="4px"
              border={
                highlightedSections?.includes('header')
                  ? 'purple'
                  : 'transparent'
              }
              style={{
                backgroundColor: headerColor,
                borderRadius:
                  whiteHeader || highlightedSections?.includes('header')
                    ? 'var(--border-radius-lg)'
                    : 'var(--border-radius-lg) var(--border-radius-lg) 0 0',
              }}
            >
              <HeaderSkeleton
                headerIconColor={headerIconColor}
                primaryIconColor={primaryIconColor}
                isMobile={isMobile}
                logoUrl={logoUrl || ''}
                searchPosition={searchConfig?.position}
                whiteHeader={Boolean(whiteHeader)}
              />
            </Flex>

            {sectionsOrder.map((key) => (
              <Fragment key={key}>{sortableSections[key]}</Fragment>
            ))}

            {/* Filter bar */}
            <Flex
              id="filters"
              data-testid="filters"
              justifyContent="space-between"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderRadius="lg"
              borderWidth="4px"
              border={
                highlightedSections?.includes('filters')
                  ? 'purple'
                  : 'transparent'
              }
            >
              <FilterBarSkeleton
                isMobile={isMobile}
                iconColor={primaryIconColor}
                searchPosition={searchConfig?.position}
              />
            </Flex>

            {/* Carousel controls */}
            <Flex
              id="carousel_controls"
              data-testid="carousel_controls"
              alignItems="center"
              justifyContent="space-between"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
            >
              <Flex gap={8} flexDirection="column">
                <PreviewSkeleton
                  height={isMobile ? TEXT_HEIGHT : 32}
                  width={isMobile ? 108 : 156}
                  mb={8}
                />
                <PreviewSkeleton height={TEXT_HEIGHT} width={72} />
              </Flex>

              <Flex gap={16}>
                <PreviewSkeleton height={TEXT_HEIGHT} width={24} />
                <PreviewSkeleton height={TEXT_HEIGHT} width={24} mr={8} />
                <PreviewSkeleton height={TEXT_HEIGHT} width={72} />
              </Flex>
            </Flex>

            {/* Product cards */}
            <Box
              id="product_cards"
              data-testid="product_cards"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
              borderRadius="lg"
              borderWidth="4px"
              border={
                highlightedSections?.includes('product_cards')
                  ? 'purple'
                  : 'transparent'
              }
              width="100%"
            >
              <CardCarousel
                cardWidth={
                  PRODUCT_CARD_SIZE_MAP[
                    productCardsConfig?.card_size || 'medium'
                  ]
                }
                cardWidthMobile={MOBILE_PRODUCT_CARD_WIDTH}
                cardGap={24}
                cardGapMobile={16}
                hideCarouselControls
                mode={isMobile ? 'max-width' : 'ideal-width'}
                // Offset margin in card carousel header
                // only appearing because we set no name
                mt={-24}
                name=""
                // Suppress a11y warning for empty heading
                headingAs="p"
              >
                {Array.from({ length: 6 }).map((_, i) => (
                  <CardCarousel.Card key={i}>
                    <ProductCardSkeleton {...productCardsConfig} />
                  </CardCarousel.Card>
                ))}
              </CardCarousel>
            </Box>

            {/* Footer */}
            <Box
              id="footer"
              data-testid="footer"
              px={HORIZONTAL_PADDING}
              py={VERTICAL_PADDING}
            >
              <FooterSkeleton isMobile={isMobile} />
            </Box>
          </Flex>
        </Card>
      </Flex>
    </ReeferThemeProvider>
  );
};
